import React from "react";
import { Search, Clock, TriangleAlert, Route as RouteIcon, CloudLightning, ChevronRight, Waves, Sun } from "lucide-react";
import Dial from "./Dial";
import CorridorHotspots from "./CorridorHotspots";
import MoodPulse from "./MoodPulse";
import StormBanner from "./StormBanner";
import { weatherIcon } from "./weatherMeta";

const ISLAND_HEADLINE = {
  green: "Roads are flowing across the island.",
  amber: "Moderate pressure building on the roads.",
  red: "Heavy conditions island-wide right now.",
};
const RISK_COLOR = { low: "#22E0A1", medium: "#FFC726", high: "#FF5C7A" };

const Tile = ({ Icon, label, value, sub, accent = "#5B9BFF", onClick, testid }) => (
  <button onClick={onClick} className="go-card relative overflow-hidden rounded-3xl p-4 text-left transition-transform active:scale-[0.97]" data-testid={testid}>
    <span className="absolute left-0 top-0 h-full w-1" style={{ background: accent }} />
    <div className="flex items-start justify-between">
      <div className="flex h-9 w-9 items-center justify-center rounded-2xl" style={{ background: `${accent}22` }}>
        <Icon size={18} color={accent} strokeWidth={2.4} />
      </div>
      <ChevronRight size={15} className="text-[#3E4E6B]" />
    </div>
    <div className="mt-3 font-head text-xl font-bold text-white">{value}</div>
    <div className="text-xs font-medium text-[#67799A]">{label}</div>
    {sub && <div className="mt-1 text-[11px] font-semibold" style={{ color: accent }}>{sub}</div>}
  </button>
);

const HomeScreen = ({ summary, weather, loading, trip, onNavigate, onOpenAlerts, onOpenTravel }) => {
  const islandDial = summary ? summary.dial : "amber";
  const level = trip ? trip.level : islandDial;
  const headline = trip ? trip.headline : summary ? ISLAND_HEADLINE[islandDial] : "Reading the roads…";
  const sub = trip
    ? `${trip.dest ? `To ${trip.dest} · ` : ""}${trip.advice}`
    : summary?.worst_route?.name
    ? `Busiest corridor: ${summary.worst_route.name} (+${summary.worst_route.delay_minutes} min).`
    : "Live island-wide conditions from the National Command Centre.";

  const adv = summary?.advisory;
  const WIcon = weather ? weatherIcon(weather.icon) : Sun;
  const stormActive = weather && ["watch", "warning"].includes(weather.storm_status);
  const busyCount = (summary?.corridors || []).filter((c) => c.dial !== "green").length;
  const incidents = summary?.active_incident_count ?? 0;

  return (
    <div className="go-scroll min-h-full overflow-y-auto pb-28">
      {/* HERO */}
      <div className="go-hero rounded-b-[2.25rem] px-5 pb-16 pt-[max(env(safe-area-inset-top),18px)] text-white">
        <header className="go-fade-up flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#FFC726]">
              <span className="go-blip inline-block h-1.5 w-1.5 rounded-full bg-[#FFC726]" /> Barbados · Live
            </div>
            <h1 className="font-head mt-1 text-2xl font-extrabold text-white">Should I leave now?</h1>
          </div>
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/12 ring-1 ring-white/20">
            <i className="fa-solid fa-shield-halved text-lg text-[#FFC726]" />
          </div>
        </header>
        <div className="go-gold-rule mt-3 h-0.5 w-24 rounded-full" />

        {weather && (
          <button onClick={onOpenTravel} className="go-fade-up mt-4 inline-flex items-center gap-2 rounded-full bg-white/12 px-3.5 py-2 ring-1 ring-white/20 backdrop-blur transition-transform active:scale-95" data-testid="home-weather-chip">
            <WIcon size={18} color="#FFC726" strokeWidth={2.2} />
            <span className="text-sm font-bold text-white">{Math.round(weather.temperature_c)}°</span>
            <span className="text-sm text-white/80">{weather.condition}</span>
            {stormActive && (
              <span className="ml-1 rounded-full px-2 py-0.5 text-[10px] font-extrabold" style={{ background: weather.storm_status === "warning" ? "#EF4444" : "#FFC726", color: weather.storm_status === "warning" ? "#fff" : "#0A2A6B" }}>
                {weather.storm_status === "warning" ? "WARNING" : "WATCH"}
              </span>
            )}
          </button>
        )}

        {adv && (
          <button onClick={onOpenAlerts} className="go-fade-up mt-4 flex w-full items-center gap-3 rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-left backdrop-blur transition-transform active:scale-[0.98]" data-testid="home-advisory-banner">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ background: adv.severity === "emergency" ? "#EF4444" : "#FFC726" }}>
              <TriangleAlert size={16} color={adv.severity === "emergency" ? "#fff" : "#0A2A6B"} strokeWidth={2.6} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold text-white">{adv.title}</div>
              <div className="truncate text-xs text-white/70">{adv.target}</div>
            </div>
            <ChevronRight size={18} color="#fff" />
          </button>
        )}
      </div>

      {/* BODY */}
      <div className="px-5">
        <div className="go-fade-up -mt-12 go-card rounded-[2rem] p-6" style={{ boxShadow: "0 14px 44px rgba(0,0,0,0.4)" }}>
          <Dial level={level} headline={headline} sub={sub} eta={trip?.eta} loading={loading && !summary} />
        </div>

        {stormActive && <div className="mt-4"><StormBanner status={weather.storm_status} summary={weather.storm_summary} /></div>}

        <button onClick={() => onNavigate()} className="go-fade-up mt-5 go-card flex w-full items-center gap-3 rounded-full px-5 py-4 transition-transform active:scale-[0.98]" data-testid="home-where-to">
          <Search size={20} color="#5B9BFF" strokeWidth={2.5} />
          <span className="text-base font-semibold text-[#67799A]">Where to?</span>
          <span className="ml-auto rounded-full bg-[#1E4DB7] px-4 py-1.5 text-sm font-bold text-white">Plan trip</span>
        </button>

        <h3 className="font-head mb-3 mt-7 text-sm font-bold uppercase tracking-wide text-[#67799A]">Island status</h3>
        <div className="go-grid2">
          <Tile Icon={RouteIcon} label="Overall traffic" value={summary?.overall_status || "—"}
            sub={summary ? `Risk: ${summary.overall_risk}` : null} accent={RISK_COLOR[summary?.overall_risk] || "#5B9BFF"}
            onClick={() => onNavigate()} testid="stat-overall" />
          <Tile Icon={Clock} label="Avg island delay" value={summary ? `${summary.avg_delay_minutes} min` : "—"}
            sub={summary ? `${busyCount} corridors busy` : null} accent="#5B9BFF" onClick={() => onNavigate()} testid="stat-delay" />
          <Tile Icon={TriangleAlert} label="Active incidents" value={incidents}
            sub={incidents > 0 ? "Tap to view alerts" : "None reported"} accent={incidents > 0 ? "#FFC726" : "#22E0A1"}
            onClick={onOpenAlerts} testid="stat-incidents" />
          <Tile Icon={stormActive ? CloudLightning : (weather ? WIcon : Waves)} label="Weather"
            value={weather ? `${Math.round(weather.temperature_c)}°` : (summary?.storm_status && summary.storm_status !== "clear" ? "Storm" : "—")}
            sub={weather ? weather.condition : "Tap for forecast"} accent={stormActive ? "#FF5C7A" : "#5B9BFF"}
            onClick={onOpenTravel} testid="stat-weather" />
        </div>

        <CorridorHotspots corridors={summary?.corridors || []} onNavigate={onNavigate} />
        <MoodPulse mood={summary?.mood} loading={loading && !summary} />

        <p className="mt-7 text-center text-xs text-[#4F6180]">Live intelligence · Government of Barbados National Command Centre</p>
      </div>
    </div>
  );
};

export default HomeScreen;
